import { Text } from '@teamsparta/stack-text';
import { vars } from '@teamsparta/stack-tokens';
import { AnimatePresence } from 'framer-motion';
import { type ReactNode, useCallback, useState } from 'react';

import Portal from '@/components/common/Portal';
import { createContext } from '@/lib/utils/context/createContext';

import * as S from './style';

const [ToastContextProvider, useToast] = createContext<{
  show: ({}: { message: string; duration?: number; icon?: ReactNode }) => void;
}>('ToastContext');

type Toast = {
  id: string;
  icon?: ReactNode;
  message: string;
};

export { useToast };

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback(
    ({
      icon,
      message,
      duration = 2500,
    }: {
      message: string;
      duration?: number;
      icon?: ReactNode;
    }) => {
      const id = Date.now().toString();

      setToasts((prev) => [...prev, { id, message, icon }]);

      setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
      }, duration);
    },
    [],
  );

  return (
    <ToastContextProvider show={show}>
      {children}
      <Portal selector='#portal'>
        <AnimatePresence>
          {toasts.map((toast) => (
            <S.Toast
              key={toast.id}
              initial={{ opacity: 0, y: 100, x: '-50%' }}
              animate={{ opacity: 1, y: 0, x: '-50%' }}
              exit={{ opacity: 0, y: 100, x: '-50%' }}
              transition={{ duration: 0.3 }}
            >
              {toast?.icon}
              <Text
                as='p'
                font='captionSb'
                color={vars.neutral[0]}
                style={{ whiteSpace: 'pre' }}
              >
                {toast?.message}
              </Text>
            </S.Toast>
          ))}
        </AnimatePresence>
      </Portal>
    </ToastContextProvider>
  );
}
